import React, { CSSProperties, memo, useEffect, useState } from 'react';
import styles from './QueueHeader.module.scss';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { HeaderCalendarIcon } from '../../../assets/svgComponents/HeaderCalendarIcon';
import { HeaderClockIcon } from '../../../assets/svgComponents/HeaderClockIcon';
import classNames from 'classnames';
import { isQueueScreenDarkTheme } from '../../../constants/API';
import { baseSoundURL } from '../../../config';
import tehnoSkyTerminalLogo from '../../../assets/icons/tehnoSkyTerminalLogo.svg';
import {
  TerminalAppearanceSettings,
  TerminalTabloElementSettings
} from '../../../types/Admin/TerminalAppearanceTypes';
import { isAndroidTabloDevice } from '../../../utils/QueuePage/tabloDevice';

const isAndroidTablo = isAndroidTabloDevice();

const normalizeMediaUrl = (url?: string | null) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url) || url.startsWith('//')) {
    return url;
  }
  const separator = url.startsWith('/') ? '' : '/';
  return `${baseSoundURL}${separator}${url}`;
};

const getLogoBackgroundSize = (fitMode: string | null | undefined, scale: number) => {
  switch (fitMode) {
    case 'contain':
      return 'contain';
    case 'cover':
      return 'cover';
    case 'stretch':
      return '100% 100%';
    case 'width':
      return '100% auto';
    case 'height':
      return 'auto 100%';
    case 'none':
      return 'auto';
    case 'scale':
    default:
      return `${scale}% ${scale}%`;
  }
};

type QueueHeaderProps = {
  logoSettings?: TerminalAppearanceSettings['logo'] | null;
  tabloHeader?: TerminalTabloElementSettings | null;
  containerStyle?: CSSProperties;
  textStyle?: CSSProperties;
};

export const QueueHeader = memo(function QueueHeader({
  logoSettings,
  tabloHeader,
  containerStyle,
  textStyle
}: QueueHeaderProps) {
  const [time, setTime] = useState<string>();
  const [date, setDate] = useState<string>();

  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const nextTime = format(now, 'HH:mm');
      const nextDate = format(now, 'd MMMM yyyy', { locale: ru });
      setTime((prev) => (prev === nextTime ? prev : nextTime));
      setDate((prev) => (prev === nextDate ? prev : nextDate));
    };
    tick();
    // На Android TV секундный таймер конкурирует с video decode — раз в 15с достаточно для HH:mm
    const intervalId = setInterval(tick, isAndroidTablo ? 15000 : 1000);
    return () => clearInterval(intervalId);
  }, []);

  const logoUrl = normalizeMediaUrl(logoSettings?.url) || tehnoSkyTerminalLogo;
  const s = logoSettings?.scale;
  const px = logoSettings?.positionX;
  const py = logoSettings?.positionY;
  const logoScale = Number.isFinite(Number(s)) ? Number(s) : 100;
  const logoPositionX = Number.isFinite(Number(px)) ? Number(px) : 50;
  const logoPositionY = Number.isFinite(Number(py)) ? Number(py) : 50;
  const logoFitMode = logoSettings?.fitMode ?? 'scale';
  const resolvedTextStyle: CSSProperties = {
    fontFamily: tabloHeader?.fontFamily || undefined,
    fontWeight: tabloHeader?.fontWeight || undefined,
    fontSize: tabloHeader?.fontSize ? `${tabloHeader.fontSize}px` : undefined,
    color: tabloHeader?.textColor || undefined,
    ...textStyle
  };
  const resolvedIconColor =
    typeof resolvedTextStyle.color === 'string' ? resolvedTextStyle.color : undefined;
  const headerBlur = Number.isFinite(tabloHeader?.backdropBlur)
    ? Number(tabloHeader?.backdropBlur)
    : null;
  const backdropStyle: CSSProperties = {
    backgroundColor: tabloHeader?.backgroundColor || undefined,
    backdropFilter: headerBlur !== null ? `blur(${headerBlur}px)` : undefined,
    WebkitBackdropFilter: headerBlur !== null ? `blur(${headerBlur}px)` : undefined
  };

  return (
    <header
      className={classNames(styles.headerContainer, {
        [styles.darkThemeHeaderContainer]: isQueueScreenDarkTheme
      })}
      style={{ ...backdropStyle, ...containerStyle }}>
      <div
        className={styles.logoImage}
        style={{
          backgroundImage: `url(${logoUrl})`,
          backgroundSize: getLogoBackgroundSize(logoFitMode, logoScale),
          backgroundPosition: `${logoPositionX}% ${logoPositionY}%`
        }}
        aria-label="Логотип очереди"
      />
      <div className={styles.dateTimeContainer}>
        <div className={styles.timeSectionComponent}>
          <HeaderCalendarIcon color={resolvedIconColor} />
          <div
            className={classNames(styles.date, {
              [styles.darkThemeText]: isQueueScreenDarkTheme
            })}>
            <span style={resolvedTextStyle}>{date}</span>
          </div>
        </div>

        <div className={styles.timeSectionComponent}>
          <HeaderClockIcon color={resolvedIconColor} />
          <div
            className={classNames(styles.time, {
              [styles.darkThemeText]: isQueueScreenDarkTheme
            })}>
            <span style={resolvedTextStyle}>{time}</span>
          </div>
        </div>
      </div>
    </header>
  );
});

export default QueueHeader;
